// Utility functions for better performance and reliability

// Sanitize image URLs to use HTTPS and proper domain
export const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  // Filter out placeholder images
  if (url.includes('tst.jpg') || url.includes('placeholder') || url.trim() === '') {
    return null;
  }
  
  // Convert HTTP/HTTPS IP addresses to CDN domain
  if (url.includes('40.40.69.79:3059')) {
    url = url.replace(/http?:\/\/40\.40\.69\.69:3059\/image/g, 'http://cdn.seritonex.com/image');
  }
  
  if (url.includes('cdn.seritonex.com/image')) {
    // Already using CDN, just ensure HTTPS
    url = url.replace('http://cdn.seritonex.com', 'https://cdn.seritonex.com');
  }
  
  // Convert any HTTP Vecteezy URLs to HTTPS
  if (url.startsWith('http://static.vecteezy.com')) {
    url = url.replace('http://static.vecteezy.com', 'https://static.vecteezy.com');
  }
  
  // Convert any other HTTP URLs to HTTPS (except localhost)
  if (url.startsWith('http://') && !url.includes('localhost') && !url.includes('127.0.0.1')) {
    url = url.replace('http://', 'https://');
  }
  
  return url;
};

// Enhanced logging utility with different levels
export const logger = {
  info: (message, data = {}) => {
    if (process.env.NODE_ENV === 'development') {
    }
  },
  warn: (message, data = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[WARN] ${message}`, maskSensitive(data));
    }
  },
  error: (message, data = {}) => {
    console.error(`[ERROR] ${message}`, maskSensitive(data));
    // In production, you might want to send errors to a service like Sentry
    if (process.env.NODE_ENV === 'production') {
      // Optional: Send to error tracking service
    }
  },
  debug: (message, data = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[DEBUG] ${message}`, maskSensitive(data));
    }
  }
};

// Keys that are considered sensitive and should be redacted from logs
const SENSITIVE_KEYS = new Set([
  'password',
  'secret',
  'pwd',
  'pass',
  'newPassword',
  'currentPassword',
  'token',
  'authToken',
]);

// Replace sensitive values with a redacted placeholder in data structures
export const maskSensitive = (obj) => {
  try {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;

    const clone = Array.isArray(obj) ? [] : {};

    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const val = obj[key];
      if (!val) {
        clone[key] = val;
        continue;
      }

      if (SENSITIVE_KEYS.has(key) || /password|secret|token|auth/i.test(key)) {
        // For strings, keep length hint but redact content
        if (typeof val === 'string') {
          const lengthHint = Math.min(val.length, 8);
          clone[key] = `[REDACTED:${lengthHint}]`;
        } else {
          clone[key] = '[REDACTED]';
        }
      } else if (typeof val === 'object') {
        clone[key] = maskSensitive(val);
      } else {
        clone[key] = val;
      }
    }

    return clone;
  } catch (err) {
    return '[UNABLE TO MASK]';
  }
};

// Retry utility function for handling failed operations
export const withRetry = async (fn, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      logger.warn(`Attempt ${i + 1} failed, retrying...`, { error: error.message });
      
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  }
  
  throw lastError;
};

// Debounce function for performance optimization
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle function for limiting function calls
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Deep clone utility
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

// Format currency with better localization support
export const formatCurrency = (amount, currency = 'ZAR', locale = 'en-ZA') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${currency === 'ZAR' ? 'R' : currency} 0.00`;
  }
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch (error) {
    // Fallback for unsupported currencies/locales
    return `${currency === 'ZAR' ? 'R' : currency} ${Number(amount).toFixed(2)}`;
  }
};

// Format date with better handling
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  
  try {
    return dateObj.toLocaleDateString('en-ZA', { ...defaultOptions, ...options });
  } catch (error) {
    return dateObj.toISOString().split('T')[0];
  }
};

// Validate email with better regex
export const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

// Validate South African phone number
export const isValidSAPhone = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  // SA numbers: 10 digits starting with 0, or 11 digits starting with 27
  return /^(0[1-9]\d{8}|27[1-9]\d{8})$/.test(cleanPhone);
};

// Validate South African postal code
export const isValidSAPostalCode = (postalCode) => {
  const cleanCode = postalCode.replace(/\D/g, '');
  return /^\d{4}$/.test(cleanCode);
};

// Generate unique ID
export const generateId = (prefix = '') => {
  return `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Sanitize string for safe HTML display
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

// Calculate VAT amount
export const calculateVAT = (amount, vatRate = 0.15) => {
  const numAmount = Number(amount) || 0;
  const numVatRate = Number(vatRate) || 0;
  return numAmount * numVatRate;
};

// Calculate price excluding VAT
export const calculatePriceExcludingVAT = (priceIncludingVAT, vatRate = 0.15) => {
  const numPrice = Number(priceIncludingVAT) || 0;
  const numVatRate = Number(vatRate) || 0;
  return numPrice / (1 + numVatRate);
};

// Calculate price including VAT
export const calculatePriceIncludingVAT = (priceExcludingVAT, vatRate = 0.15) => {
  const numPrice = Number(priceExcludingVAT) || 0;
  const numVatRate = Number(vatRate) || 0;
  return numPrice * (1 + numVatRate);
};

// Safe JSON parse
export const safeJSONParse = (str, defaultValue = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return defaultValue;
  }
};

// Safe JSON stringify
export const safeJSONStringify = (obj, defaultValue = '{}') => {
  try {
    return JSON.stringify(obj);
  } catch {
    return defaultValue;
  }
};

// Get nested object property safely
export const getNestedProperty = (obj, path, defaultValue = undefined) => {
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result === null || result === undefined || typeof result !== 'object') {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result !== undefined ? result : defaultValue;
};

// Set nested object property safely
export const setNestedProperty = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  
  let current = obj;
  for (const key of keys) {
    if (current[key] === undefined || current[key] === null) {
      current[key] = {};
    }
    current = current[key];
  }
  
  current[lastKey] = value;
  return obj;
};

// Retry async function with exponential backoff
export const retryAsync = async (fn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (i === maxRetries) {
        throw lastError;
      }
      
      const delay = baseDelay * Math.pow(2, i);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Check if user is on mobile device
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if device supports touch
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Smooth scroll to element
export const scrollToElement = (element, options = {}) => {
  if (!element) return;
  
  const defaultOptions = {
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  };
  
  element.scrollIntoView({ ...defaultOptions, ...options });
};

// Copy text to clipboard
export const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.warn('Clipboard API failed, falling back to execCommand');
    }
  }
  
  // Fallback for older browsers
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (error) {
    document.body.removeChild(textArea);
    return false;
  }
};

// Local storage helpers with error handling
export const localStorageHelpers = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
      return false;
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  }
};

// Image optimization helpers
export const imageHelpers = {
  // Preload image
  preloadImage: (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  },
  
  // Get optimized image URL (if using a CDN)
  getOptimizedImageUrl: (src, width, height, quality = 80) => {
    if (!src) return src;
    
    // Example for Cloudinary or similar service
    // Modify this based on your image optimization service
    if (src.includes('cloudinary.com')) {
      const parts = src.split('/upload/');
      if (parts.length === 2) {
        return `${parts[0]}/upload/w_${width},h_${height},q_${quality},f_auto/${parts[1]}`;
      }
    }
    
    return src;
  }
};

// South African specific validation functions
export const validateSouthAfricanData = {
  // Validate South African ID number
  idNumber: (id) => {
    if (!id || typeof id !== 'string') return false;
    
    // Remove any spaces or dashes
    const cleanId = id.replace(/[-\s]/g, '');
    
    // Check length
    if (cleanId.length !== 13) return false;
    
    // Check if all characters are digits
    if (!/^\d{13}$/.test(cleanId)) return false;
    
    // Validate date portion (YYMMDD)
    const month = parseInt(cleanId.substring(2, 4), 10);
    const day = parseInt(cleanId.substring(4, 6), 10);
    
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    
    // Validate checksum using Luhn algorithm
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      let digit = parseInt(cleanId[i], 10);
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === parseInt(cleanId[12], 10);
  },

  // Validate South African phone number
  phoneNumber: (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    
    // Remove any spaces, dashes, or brackets
    const cleanPhone = phone.replace(/[-\s()]/g, '');
    
    // Check for South African formats
    const patterns = [
      /^27\d{9}$/, // International format
      /^0\d{9}$/, // National format
      /^\+27\d{9}$/ // International with plus
    ];
    
    return patterns.some(pattern => pattern.test(cleanPhone));
  },

  // Validate South African postal code
  postalCode: (code) => {
    if (!code || typeof code !== 'string') return false;
    
    // South African postal codes are 4 digits
    return /^\d{4}$/.test(code.trim());
  },

  // Validate South African company registration number
  companyRegistration: (regNumber) => {
    if (!regNumber || typeof regNumber !== 'string') return false;
    
    // Remove any spaces or slashes
    const cleanReg = regNumber.replace(/[-\s/]/g, '');
    
    // Format: YYYY/NNNNNN/NN
    return /^\d{4}\d{6}\d{2}$/.test(cleanReg);
  },

  // Validate South African VAT number
  vatNumber: (vat) => {
    if (!vat || typeof vat !== 'string') return false;
    
    // Remove any spaces or dashes
    const cleanVat = vat.replace(/[-\s]/g, '');
    
    // South African VAT numbers are 10 digits starting with 4
    return /^4\d{9}$/.test(cleanVat);
  },

  // Validate South African bank account number
  bankAccount: (account) => {
    if (!account || typeof account !== 'string') return false;
    
    // Remove any spaces or dashes
    const cleanAccount = account.replace(/[-\s]/g, '');
    
    // Bank account numbers are typically 8-11 digits
    return /^\d{8,11}$/.test(cleanAccount);
  }
};

// Parse discontinued value coming from API into a reliable boolean
export const parseDiscontinued = (v) => {
  try {
    const s = String(v).toLowerCase();
    return s === "true" || s === "1";
  } catch {
    return false;
  }
};
